import React, { useEffect, useState } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { api } from "../../../core/config";
import { headers } from "../../../core/store/localStorage";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";

const Tabela = () => {
  const [tableHeader, setTableHeader] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [columnTotals, setColumnTotals] = useState([]); // totais por série (técnico/cliente)
  const [grandTotal, setGrandTotal] = useState(0); // total geral
  const [lineChartData, setLineChartData] = useState([]); // Técnico x mês
  const [naturezaChartData, setNaturezaChartData] = useState([]); // Natureza x técnico / cliente
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSmall, setIsSmall] = useState(false);
  const [viewMode, setViewMode] = useState("TEC_X_MES");

  const handleChangeView = (event) => {
    setViewMode(event.target.value);
  };

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        setError(null);

        let endpoint = `${api}/v1/estatisticas/servicos-usuarios`;
        if (viewMode === "NAT_X_TEC") {
          endpoint = `${api}/v1/estatisticas/servicos-natureza`;
        } else if (viewMode === "CLI_POR_NAT") {
          endpoint = `${api}/v1/estatisticas/clientes-por-mes`;
        }

        const response = await axios.get(endpoint, headers());

        console.log(JSON.stringify(response.data));
        const dados = response.data ?? [];
        processarDados(dados, viewMode);
      } catch (err) {
        console.error("Erro ao buscar dados de serviços:", err);
        setError("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, [viewMode]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth < 768); // breakpoint ~tablet
    };

    handleResize(); // já checa ao montar
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formatMesAno = (valor) => {
    if (!valor) return valor;
    const partes = String(valor).split("-");
    if (partes.length !== 2) return valor;

    const [ano, mes] = partes;
    if (!ano || !mes) return valor;

    return `${mes.padStart(2, "0")}/${ano}`;
  };

  const processarDados = (childData, tipoView) => {
    if (!Array.isArray(childData)) return;

    if (tipoView === "CLI_POR_NAT") {
      const allMonths = new Set();

      const parsedRows = childData.map((item) => {
        const cliente = item[0];
        const mesesJson = item[1];
        const totalCliente = item[2];

        let meses = {};
        try {
          meses = JSON.parse(mesesJson || "{}");
        } catch (e) {
          console.error("Erro ao fazer parse do JSON de meses:", e);
        }

        Object.keys(meses).forEach((m) => allMonths.add(m));

        return { cliente, meses, totalCliente };
      });

      const mesesOrdenados = Array.from(allMonths).sort(); // ["2025-07", ...]
      const mesesLabels = mesesOrdenados.map((m) => formatMesAno(m));

      const header = ["Cliente", ...mesesLabels, "Total geral"];

      const mesTotals = new Array(mesesOrdenados.length).fill(0);
      let totalGeral = 0;

      const rows = parsedRows.map((row) => {
        const values = mesesOrdenados.map((mes, idx) => {
          const v = row.meses[mes] ?? 0;
          mesTotals[idx] += v;
          return v;
        });

        const totalLinha =
          row.totalCliente ?? values.reduce((acc, v) => acc + v, 0);

        totalGeral += totalLinha;

        return {
          chave: row.cliente, // primeira coluna = cliente
          values,
          total: totalLinha,
        };
      });

      setTableHeader(header);
      setTableRows(rows);
      setColumnTotals(mesTotals);
      setGrandTotal(totalGeral);

      setLineChartData([]);
      setNaturezaChartData([]);
      return;
    }

    // ---------- TÉCNICO x MÊS ou NATUREZA x TÉCNICO ----------
    // formato: [ mes/natureza, "{\"Tec1\":3,...}", total ]
    // ---------- TÉCNICO x MÊS ----------
    const allTechnicians = new Set();

    const parsedRows = childData.map((item) => {
      const chaveBruta = item[0]; // Aqui vem o número do mês (ex: 7, 8... 12, 1)
      const tecnicosJson = item[1];
      const total = item[2];

      let tecnicos = {};
      try {
        tecnicos = JSON.parse(tecnicosJson || "{}");
      } catch (e) {
        console.error("Erro ao fazer parse do JSON de técnicos:", e);
      }

      Object.keys(tecnicos).forEach((nome) => allTechnicians.add(nome));

      return { chaveBruta, tecnicos, total };
    });

    const technicians = Array.from(allTechnicians);
    const firstColumnLabel = tipoView === "TEC_X_MES" ? "Mês" : "Natureza";
    const header = [firstColumnLabel, ...technicians, "Total geral"];

    const technicianTotals = new Array(technicians.length).fill(0);
    let totalGeral = 0;

    const chartMatrix = [[firstColumnLabel, ...technicians]];

    const rows = parsedRows.map((row) => {
      const values = technicians.map((nome, idx) => {
        const v = row.tecnicos[nome] ?? 0;
        technicianTotals[idx] += v;
        return v;
      });

      const totalLinha =
        row.total != null ? row.total : values.reduce((acc, v) => acc + v, 0);
      totalGeral += totalLinha;

      // --- A MUDANÇA ESTÁ AQUI ---
      // Convertendo para String, o Google Charts para de tentar ordenar numericamente
      // e respeita a ordem que veio do SQL (7, 8, 9, 10, 11, 12, 1)
      const chaveFormatada = String(row.chaveBruta);

      chartMatrix.push([chaveFormatada, ...values]);

      return {
        chave: chaveFormatada,
        values,
        total: totalLinha,
      };
    });

    setTableHeader(header);
    setTableRows(rows);
    setColumnTotals(technicianTotals);
    setGrandTotal(totalGeral);

    if (tipoView === "TEC_X_MES") {
      setLineChartData(chartMatrix);
      setNaturezaChartData([]);
    } else {
      setNaturezaChartData(chartMatrix);
      setLineChartData([]);
    }
  };

  // ---- estilos (card + tabela) ----

  const cardStyle = {
    marginTop: 20,
    backgroundColor: "#ffffff",
    borderRadius: 10,
    padding: 20,
    boxShadow: "0 3px 10px rgba(15, 23, 42, 0.12)",
  };

  const titleStyle = {
    margin: 0,
    marginBottom: 2,
    fontSize: 18,
    fontWeight: 600,
    textAlign: "center",
    color: "#111827",
  };

  const subtitleStyle = {
    margin: 0,
    marginBottom: 12,
    fontSize: 13,
    color: "#6B7280",
    textAlign: "center",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 15,
    tableLayout: "fixed",
  };

  const headerCellStyle = {
    backgroundColor: "#EFF6FF",
    color: "#1F2933",
    fontWeight: 600,
    padding: "10px 12px",
    borderBottom: "1px solid #E5E7EB",
    textAlign: "center",
    whiteSpace: "nowrap",
  };

  const firstHeaderCellStyle = {
    ...headerCellStyle,
    textAlign: "left",
  };

  const cellStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #E5E7EB",
    textAlign: "center",
  };

  const firstCellStyle = {
    ...cellStyle,
    textAlign: "left",
  };

  const footerRowStyle = {
    backgroundColor: "#F3F4FF",
  };

  const footerCellStyle = {
    padding: "9px 12px",
    fontWeight: 600,
    borderTop: "2px solid #E5E7EB",
    textAlign: "center",
  };

  const firstFooterCellStyle = {
    ...footerCellStyle,
    textAlign: "left",
  };

  const getSubtitle = () => {
    if (viewMode === "TEC_X_MES") return "Chamados por mês x nome do técnico";
    if (viewMode === "NAT_X_TEC") return "Natureza dos chamados x técnico";
    return "Clientes x mês";
  };

  const getEmptyNaturezaText = () => {
    if (viewMode === "NAT_X_TEC")
      return 'Nenhum dado para "Natureza x técnico" ainda.';

    return "";
  };

  return (
    <>
      <div style={cardStyle}>
        <div
          style={{
            display: "flex",
            flexDirection: isSmall ? "column" : "row", // 👈 empilha em telas pequenas
            alignItems: isSmall ? "stretch" : "flex-start",
            justifyContent: isSmall ? "flex-start" : "space-between",
            marginBottom: 8,
            gap: isSmall ? 8 : 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                ...titleStyle,
                textAlign: isSmall ? "left" : titleStyle.textAlign,
                fontSize: isSmall ? 16 : 18, // 👈 fonte menor em telas pequenas
              }}
            >
              Distribuição de atendimentos (últimos 6 meses)
            </h3>
            <p
              style={{
                ...subtitleStyle,
                textAlign: isSmall ? "left" : subtitleStyle.textAlign,
                fontSize: isSmall ? 12 : 13, // 👈 fonte menor em telas pequenas
              }}
            >
              {getSubtitle()}
            </p>
          </div>

          <FormControl
            size="small"
            variant="outlined"
            style={{
              minWidth: isSmall ? "100%" : 260, // 👈 full width no mobile
              marginTop: isSmall ? 4 : 0,
            }}
          >
            <InputLabel id="view-mode-label">Visualização</InputLabel>
            <Select
              labelId="view-mode-label"
              value={viewMode}
              label="Visualização"
              onChange={handleChangeView}
            >
              <MenuItem value="TEC_X_MES">Técnico x mês</MenuItem>
              <MenuItem value="NAT_X_TEC">Natureza x técnico</MenuItem>
              <MenuItem value="CLI_POR_NAT">Clientes x mês</MenuItem>
            </Select>
          </FormControl>
        </div>

        {loading && <div>Carregando dados...</div>}
        {error && (
          <div style={{ color: "red", textAlign: "center" }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            {viewMode === "TEC_X_MES" && lineChartData.length > 1 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ width: "100%", display: "grid", gap: 2 }}>
                  <div
                    style={{
                      width: "100%",
                      minWidth: 0,
                      overflowX: "auto",
                      maxHeight: 500,
                      overflowY: "auto",
                    }}
                  >
                    <div style={{ minWidth: 1200 }}>
                      <Chart
                        width={"100%"}
                        height={"320px"}
                        chartType="LineChart"
                        loader={<div>Carregando gráfico...</div>}
                        data={lineChartData}
                        options={{
                          backgroundColor: "#ffffff",
                          legend: { position: "bottom" },
                          chartArea: { width: "80%", height: "65%" },
                          hAxis: {
                            title: "Mês",
                          },
                          vAxis: {
                            title: "Quantidade de atendimentos",
                            minValue: 0,
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {viewMode !== "TEC_X_MES" && naturezaChartData.length <= 1 && (
              <div
                style={{
                  textAlign: "center",
                  marginBottom: 24,
                  fontSize: 13,
                  color: "#6B7280",
                }}
              >
                {getEmptyNaturezaText()}
              </div>
            )}
          </>
        )}

        {!loading && !error && tableHeader.length > 0 && (
          <div style={{ width: "100%", display: "grid", overflowX: "auto" }}>
            <div
              style={{
                overflowX: "auto",
                display: "flex",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <table
                style={{
                  ...tableStyle,
                  minWidth: "1200px",
                }}
              >
                <thead>
                  <tr>
                    {tableHeader.map((col, idx) => (
                      <th
                        key={idx}
                        style={
                          idx === 0 ? firstHeaderCellStyle : headerCellStyle
                        }
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row, idx) => {
                    const isZebra = idx % 2 === 1;
                    const rowStyle = {
                      backgroundColor: isZebra ? "#F9FAFB" : "#FFFFFF",
                    };

                    return (
                      <tr key={idx} style={rowStyle}>
                        <td style={firstCellStyle}>{row.chave}</td>{" "}
                        {/* 👈 sempre chave */}
                        {row.values.map((v, i) => (
                          <td key={i} style={cellStyle}>
                            {v}
                          </td>
                        ))}
                        <td style={cellStyle}>{row.total}</td>
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  <tr style={footerRowStyle}>
                    <td style={firstFooterCellStyle}>
                      <strong>Total geral</strong>
                    </td>
                    {columnTotals.map((tot, idx) => (
                      <td key={idx} style={footerCellStyle}>
                        <strong>{tot}</strong>
                      </td>
                    ))}
                    <td style={footerCellStyle}>
                      <strong>{grandTotal}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {!loading && !error && tableHeader.length === 0 && (
          <div style={{ textAlign: "center" }}>Nenhum dado encontrado.</div>
        )}
      </div>
    </>
  );
};

export default Tabela;
